import { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import type { FolderResponse } from "@redotech/effect-api-shared/rpc/view-folder";
import { useRequiredContext } from "@redotech/react-util/context";
import QuestionCircleIcon from "@redotech/redo-icons/help-circle.svg";
import TicketIcon from "@redotech/redo-icons/ticket-02.svg";
import TicketFilledIcon from "@redotech/redo-icons/ticket-02_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { ConversationCountsContext } from "@redotech/redo-merchant-support-app/services/conversation-counts-service";
import { SupportViewOrderingContext } from "@redotech/redo-merchant-support-app/services/support-view-ordering-service";
import { ViewInfo } from "@redotech/redo-merchant-support-app/views";
import {
  SettingsPermissions,
  SupportPermissions,
} from "@redotech/redo-model/permissions";
import type { SupportFolder as SupportFolderType } from "@redotech/redo-model/support-folder";
import type { FilterGroup } from "@redotech/redo-model/views/views";
import { encodeAdvancedFilterGroup } from "@redotech/redo-web/advanced-filters/advanced-filter";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import {
  RedoNavDraggableList,
  RedoNavDraggableListItem,
} from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-nav-draggable-list";
import { RedoNavFolder } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-nav-folder";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import {
  RedoSecondaryNavItem,
  useNavTreeNode,
} from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext, useMemo, useState } from "react";
import { useLocation, useMatch } from "react-router-dom";
import {
  SUPPORT_ICON,
  SUPPORT_ICON_FILLED,
  SupportSuiteStatusesContext,
} from "../../../../redo-suite/support-cloud";
import { userNameCache } from "../../../../support-v2/filters/assignees-filter";
import { useConversationCounts } from "../../../../support-v2/hooks/use-conversation-counts";
import { useSupportV2FolderActions } from "../../../../support-v2/hooks/use-support-v2-folder-actions";
import { useSupportV2ViewsAndFolders } from "../../../../support-v2/hooks/use-support-v2-views-and-folders";
import {
  buildAssignedViewFilterGroup,
  DEFAULT_VIEW_DISPLAY_NAMES,
  DefaultView,
  OPEN_VIEW_FILTER_GROUP,
} from "../../../../support-v2/views/default-views";
import { SupportV2FolderModal } from "../../../../support-v2/views/folders/support-v2-folder-modal";
import { SupportAnalyticsItems } from "./analytics";
import { ConciergeItems } from "./concierge";
import { DevItems } from "./dev";
import { useSupportNotifications } from "./support-notifications";

export function useSupportNavigation(): PageNavigationEntry | null {
  const routes = useRequiredContext(MerchantRoutesContext);
  const { supportEnabled } = useRequiredContext(SupportSuiteStatusesContext);

  const canViewSupport = useHasPermission(SupportPermissions.VIEW_SUPPORT);

  const conversationCounts = useRequiredContext(ConversationCountsContext);

  useSupportNotifications();

  const hasAnyNonZeroCounts = useMemo(() => {
    return Object.values(conversationCounts).some((count) => count > 0);
  }, [conversationCounts]);

  if (!canViewSupport) {
    return null;
  }

  const baseUrl = routes.supportBaseUrl;
  const primaryUrl = routes.getSupportHref({ enabled: supportEnabled });

  const primaryTab: LinkPrimaryTab = {
    id: "support",
    hasNotification: hasAnyNonZeroCounts,
    UnfilledIcon: SUPPORT_ICON,
    FilledIcon: SUPPORT_ICON_FILLED,
    type: "link",
    pathRoot: baseUrl,
    href: primaryUrl,
    friendlyName: "Support",
  };

  const secondaryNavigationData: SecondaryNavigationData = {
    id: "support",
    name: "Support",
    NavContent: SecondarySupportSection,
  };

  return { primaryTab, secondaryNavigationData };
}

const SecondarySupportSection = memo(function SecondarySupportSection() {
  const { id: nodeId, TreeContext } = useNavTreeNode({
    friendlyName: "Support",
  });

  const matchSupportV2Wildcard = !!useMatch("/stores/:storeId/support-v2/*");
  const matchSupportV2Root = !!useMatch("/stores/:storeId/support-v2");
  const isOnSupportV2 = matchSupportV2Wildcard || matchSupportV2Root;

  return (
    <TreeContext.Provider value={nodeId}>
      <SupportAnalyticsItems />
      <ConciergeItems />
      <DevItems />
      <FaqItems />
      {isOnSupportV2 ? <SupportV2TicketsItem /> : <SupportTicketsItem />}
    </TreeContext.Provider>
  );
});

function buildDefaultViewHref(
  teamId: string,
  defaultView: DefaultView,
  filterGroup: FilterGroup,
): string {
  const base = `/stores/${teamId}/support-v2/`;
  const params = new URLSearchParams();
  params.set("defaultView", defaultView);
  try {
    const encoded = encodeAdvancedFilterGroup(filterGroup);
    params.set("fg", encoded);
  } catch {
    // ignore encoding errors
  }
  return `${base}?${params.toString()}`;
}

function buildViewHref(
  teamId: string,
  viewId: string,
  filterGroup: unknown,
): string {
  const base = `/stores/${teamId}/support-v2/`;
  const params = new URLSearchParams();
  params.set("view", viewId);
  if (filterGroup) {
    try {
      const encoded = encodeAdvancedFilterGroup(filterGroup as FilterGroup);
      params.set("fg", encoded);
    } catch {
      // ignore encoding errors
    }
  }
  return `${base}?${params.toString()}`;
}

const SupportV2TicketsItem = memo(function SupportV2TicketsItem() {
  return <SupportV2TicketsItemContent />;
});

const SupportV2TicketsItemContent = memo(
  function SupportV2TicketsItemContent() {
    const team = useRequiredContext(TeamContext);
    const user = useContext(UserContext);
    const location = useLocation();
    const { views, folders } = useSupportV2ViewsAndFolders();
    const { createFolder, updateFolder, deleteFolder } =
      useSupportV2FolderActions();
    const countsMap = useConversationCounts(views, user?._id);

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [folderBeingEdited, setFolderBeingEdited] =
      useState<FolderResponse | null>(null);

    const reservedFolderNames = useMemo(
      () => new Set(folders.map((f) => f.name)),
      [folders],
    );

    const folderlessViews = useMemo(
      () => views.filter((v) => !v.folderId),
      [views],
    );

    const matchChildPath = !!useMatch(`/stores/${team._id}/support-v2/*`);
    const matchRootPath = !!useMatch(`/stores/${team._id}/support-v2`);
    const anyChildSelected = matchChildPath || matchRootPath;

    const currentSearchParams = new URLSearchParams(location.search);
    const currentViewId = currentSearchParams.get("view");
    const currentDefaultView = currentSearchParams.get("defaultView");

    const assignedFilterGroup = useMemo(() => {
      if (!user?._id) return null;
      if (user.name) {
        userNameCache.set(MongoUserId.fromString(user._id), user.name);
      }
      return buildAssignedViewFilterGroup(user._id);
    }, [user?._id, user?.name]);

    return (
      <>
        <RedoSecondaryNavItem
          action={{ type: "link", href: `/stores/${team._id}/support-v2/` }}
          anyChildSelected={anyChildSelected}
          FilledIcon={TicketFilledIcon}
          friendlyName="Tickets"
          id="tickets"
          UnfilledIcon={TicketIcon}
        >
          <RedoSecondaryNavItem
            action={{
              type: "link",
              href: buildDefaultViewHref(
                team._id,
                DefaultView.OPEN,
                OPEN_VIEW_FILTER_GROUP,
              ),
            }}
            friendlyName={DEFAULT_VIEW_DISPLAY_NAMES[DefaultView.OPEN]}
            id={DefaultView.OPEN}
            notificationCount={countsMap.get(DefaultView.OPEN) ?? 0}
            selected={currentDefaultView === DefaultView.OPEN}
          />
          {assignedFilterGroup && (
            <RedoSecondaryNavItem
              action={{
                type: "link",
                href: buildDefaultViewHref(
                  team._id,
                  DefaultView.ASSIGNED,
                  assignedFilterGroup,
                ),
              }}
              friendlyName={DEFAULT_VIEW_DISPLAY_NAMES[DefaultView.ASSIGNED]}
              id={DefaultView.ASSIGNED}
              notificationCount={countsMap.get(DefaultView.ASSIGNED) ?? 0}
              selected={currentDefaultView === DefaultView.ASSIGNED}
            />
          )}
          {folders.map((folder) => {
            const folderViews = views.filter(
              (v) => String(v.folderId) === String(folder.id),
            );
            return (
              <RedoNavFolder
                anyChildSelected={false}
                folderEditRequested={() => setFolderBeingEdited(folder)}
                folderId={String(folder.id)}
                folderName={folder.name}
                key={String(folder.id)}
              >
                {folderViews.map((view) => (
                  <RedoSecondaryNavItem
                    action={{
                      type: "link",
                      href: buildViewHref(
                        team._id,
                        String(view.id),
                        view.filterGroup,
                      ),
                    }}
                    friendlyName={view.name}
                    id={String(view.id)}
                    key={String(view.id)}
                    notificationCount={countsMap.get(String(view.id)) ?? 0}
                    selected={currentViewId === String(view.id)}
                  />
                ))}
              </RedoNavFolder>
            );
          })}
          {folderlessViews.map((view) => (
            <RedoSecondaryNavItem
              action={{
                type: "link",
                href: buildViewHref(
                  team._id,
                  String(view.id),
                  view.filterGroup,
                ),
              }}
              friendlyName={view.name}
              id={String(view.id)}
              key={String(view.id)}
              notificationCount={countsMap.get(String(view.id)) ?? 0}
              selected={currentViewId === String(view.id)}
            />
          ))}
          <RedoSecondaryNavItem
            action={{ type: "link", href: `/stores/${team._id}/support-v2/` }}
            friendlyName="+ Add new view"
            id="add-new-view"
            selected={false}
          />
          <RedoSecondaryNavItem
            action={{
              type: "button",
              onClick: () => setIsCreateModalOpen(true),
            }}
            friendlyName="+ Create folder"
            id="create-folder"
          />
        </RedoSecondaryNavItem>

        <SupportV2FolderModal
          currentName=""
          currentViewIds={[]}
          isOpen={isCreateModalOpen}
          mode="create"
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={async (name, selectedViewIds, isPrivate) => {
            await createFolder(name, selectedViewIds, isPrivate);
            setIsCreateModalOpen(false);
          }}
          reservedFolderNames={reservedFolderNames}
          views={views.map((v) => ({ id: String(v.id), name: v.name }))}
        />

        {folderBeingEdited && (
          <SupportV2FolderModal
            currentName={folderBeingEdited.name}
            currentViewIds={views
              .filter(
                (v) =>
                  v.folderId != null &&
                  String(v.folderId) === String(folderBeingEdited.id),
              )
              .map((v) => String(v.id))}
            isOpen
            mode="edit"
            onClose={() => setFolderBeingEdited(null)}
            onDelete={async () => {
              await deleteFolder(String(folderBeingEdited.id));
              setFolderBeingEdited(null);
            }}
            onSubmit={async (name, selectedViewIds) => {
              await updateFolder(
                String(folderBeingEdited.id),
                name,
                selectedViewIds,
              );
              setFolderBeingEdited(null);
            }}
            reservedFolderNames={reservedFolderNames}
            views={views.map((v) => ({ id: String(v.id), name: v.name }))}
          />
        )}
      </>
    );
  },
);

const SupportTicketsItem = memo(function SupportTicketsItem() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const { supportEnabled } = useRequiredContext(SupportSuiteStatusesContext);
  const conversationCounts = useRequiredContext(ConversationCountsContext);

  const {
    folderlessViewOrdering,
    setFolderBeingEdited,
    folders,
    updateViewOrdering,
    openCreateFolderModal,
  } = useRequiredContext(SupportViewOrderingContext);

  const reorderableViews: RedoNavDraggableListItem<ViewInfo>[] = useMemo(() => {
    const itemToProps = (
      view: ViewInfo,
    ): RedoNavDraggableListItem<ViewInfo> => {
      const viewNameToUse = view.identifier.name || view.title;
      const viewCountKey = view.identifier?._id
        ? view.title
        : view.identifier?.name || "";

      const unreadCount = conversationCounts[viewCountKey] ?? 0;

      return {
        id: view.identifier._id || view.identifier.name || "",
        friendlyName: view.title,
        notificationCount: unreadCount,
        itemValue: view,
        isPrivate: view.isPrivate,
        muted: view.muted,
        action: {
          type: "link",
          href: routes.getSupportTableHref(viewNameToUse),
        },
      };
    };

    return folderlessViewOrdering.map(itemToProps);
  }, [conversationCounts, folderlessViewOrdering, routes]);

  const supportBaseUrl = routes.supportBaseUrl;
  const anyChildSelected = useMatch(`${supportBaseUrl}/*`);

  if (!supportEnabled) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={TicketFilledIcon}
      friendlyName="Tickets"
      id="tickets"
      UnfilledIcon={TicketIcon}
    >
      {folders.map((folder) => (
        <SupportFolder
          folder={folder}
          folderEditRequested={() => setFolderBeingEdited(folder)}
          key={folder._id}
        />
      ))}
      <RedoNavDraggableList
        draggableItems={reorderableViews}
        setDraggableItems={(newOrder) =>
          updateViewOrdering(newOrder.map((item) => item.itemValue))
        }
      />
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.getSupportTableSubRoute("sent") }}
        friendlyName="Sent"
        id="view-sent"
      />
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.getSupportTableSubRoute("spam") }}
        friendlyName="Spam"
        id="view-spam"
      />
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getSupportTableSubRoute("create"),
        }}
        friendlyName="+ Add new view"
        id="create-view"
      />
      <RedoSecondaryNavItem
        action={{ type: "button", onClick: openCreateFolderModal }}
        friendlyName="+ Create folder"
        id="create-folder"
      />
    </RedoSecondaryNavItem>
  );
});

const SupportFolder = memo(function SupportFolder({
  folder,
  folderEditRequested,
}: {
  folder: SupportFolderType;
  folderEditRequested: () => void;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);

  const { defaultViewOrdering } = useRequiredContext(
    SupportViewOrderingContext,
  );

  const conversationCounts = useRequiredContext(ConversationCountsContext);

  const userDefinedViewsInFolder = useMemo(() => {
    const viewIds = new Set(folder.viewIds);

    return defaultViewOrdering.filter((view) =>
      view.identifier._id ? viewIds.has(view.identifier._id) : false,
    );
  }, [folder.viewIds, defaultViewOrdering]);

  const defaultViewsInFolder = useMemo(() => {
    const viewNames = new Set(folder.defaultViews);

    return defaultViewOrdering.filter((view) =>
      view.identifier.name ? viewNames.has(view.identifier.name) : false,
    );
  }, [folder.defaultViews, defaultViewOrdering]);

  const allFolderViews = useMemo(() => {
    return [...userDefinedViewsInFolder, ...defaultViewsInFolder];
  }, [userDefinedViewsInFolder, defaultViewsInFolder]);

  const location = useLocation();

  const anyChildSelected = useMemo(() => {
    return allFolderViews.some((view) => {
      const viewNameToUse = view.identifier.name || view.title;
      const link = routes.getSupportTableHref(viewNameToUse);
      return location.pathname.startsWith(link);
    });
  }, [location.pathname, allFolderViews, routes]);

  const folderChildren = allFolderViews.map((view) => {
    const uniqueId = view.identifier._id || view.identifier.name || "";

    const linkName = view.identifier.name || view.title;

    const link = routes.getSupportTableHref(linkName);

    const viewCountKey = view.identifier?._id
      ? view.title
      : view.identifier?.name || "";

    const unreadCount = conversationCounts[viewCountKey] ?? 0;

    return (
      <RedoSecondaryNavItem
        action={{ type: "link", href: link }}
        friendlyName={view.title}
        id={uniqueId}
        isPrivate={view.isPrivate}
        key={uniqueId}
        muted={view.muted}
        notificationCount={unreadCount}
      />
    );
  });

  return (
    <RedoNavFolder
      anyChildSelected={anyChildSelected}
      folderEditRequested={folderEditRequested}
      folderId={folder._id}
      folderName={folder.name}
    >
      {folderChildren}
    </RedoNavFolder>
  );
});

const FaqItems = memo(function FaqItems() {
  const routes = useRequiredContext(MerchantRoutesContext);

  const canManageFaq = useHasPermission(
    SettingsPermissions.SET_SUPPORT_SETTINGS,
  );

  const supportBaseUrl = routes.supportBaseUrl;
  const anyChildSelected = useMatch(`${supportBaseUrl}/faq/*`);

  if (!canManageFaq) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={QuestionCircleIcon}
      friendlyName="FAQ"
      id="faq"
      UnfilledIcon={QuestionCircleIcon}
    >
      <RedoSecondaryNavItem
        action={{ type: "link", href: `${supportBaseUrl}/faq/home` }}
        friendlyName="Content"
        id="faq-collections-and-articles"
      />
      <RedoSecondaryNavItem
        action={{ type: "link", href: `${supportBaseUrl}/faq/editor/theme` }}
        friendlyName="Style"
        id="faq-theme-editor"
      />
    </RedoSecondaryNavItem>
  );
});
